<template>
  <v-dialog
    v-model="dialog"
    width="700"
    content-class="new-user-dialog"
    :persistent="step === 'usersAssignment' || step === 'copyInvitation'"
  >
    <v-card class="dialog-card">
      <template v-if="step === 'question'">
        <v-card-title tag="p" class="new-user-dialog-title text-break mb-0 mt-0 text-center justify-center">
          <v-row>
            <v-col cols="8" offset="2">
              {{$t('AddUser.addUserDialog.questionTitle')}}
            </v-col>
            <v-col cols="2" class="px-0 text-end" align-self="start">
              <v-btn small icon @click="close" class="my-0">
                <v-icon color="#000000">close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-row justify="center" class="mt-2">
            <v-col align="end" cols="12" sm="6" class="py-1">
              <v-btn :disabled="!sortedOrganizationUsers.length" block color="#0098E1" class="submit-button" @click="existingUsers()">{{$t('AddUser.addUserDialog.extistingUsers')}}</v-btn>
            </v-col>
            <v-col align="start" cols="12" sm="6" class="py-1">
              <v-btn block color="#0098E1" class="submit-button" @click="createUser()">{{$t('AddUser.addUserDialog.newUser')}}</v-btn>
            </v-col>
          </v-row>
          <div v-if="fromNewCampaign && campaignEditors.length" class="campaign-editors-section mt-6">
            <p class="editors-title mb-2">{{$t('NewCampaignInitialData.devicesAndServices.editorsTitle')}}</p>
            <v-row
              v-for="editor in campaignEditors"
              :key="editor.id"
              align="center"
              no-gutters
              class="editor-invite-row"
            >
              <v-col cols="7" class="py-2 pr-2 text-left">
                <p class="mb-0 linked-user-name">{{ editor.name }} {{ editor.lastName || '' }}</p>
              </v-col>
              <v-col cols="5" class="py-2 d-flex justify-center">
                <v-btn
                  color="#0098E1"
                  small
                  outlined
                  class="copy-invitation-button"
                  :loading="copyingInvitationUserId === editor.id"
                  @click="copyInvitationLink(editor.id)"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  {{$t('NewCampaignInitialData.devicesAndServices.copyInvitationLink')}}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </template>
      <template v-else-if="step === 'usersAssignment'">
        <v-card-title tag="p" class="new-user-dialog-title text-break mb-0 mt-0 text-center justify-center">
          <v-row>
            <v-col cols="8" offset="2">
              {{$t('AddUser.addUserDialog.usersAssignmentTitle')}}
            </v-col>
            <v-col cols="2" class="px-0 text-end" align-self="start">
              <v-btn small icon @click="close" class="my-0">
                <v-icon color="#000000">close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text class="mt-0 dialog-content">
          <v-row justify="center" class="scrollable-content">
            <v-autocomplete
              v-model="assignedUsers"
              :items="sortedOrganizationUsers"
              hide-no-data
              outlined
              multiple
              filled
              hide-selected
              hide-details
              chips
              :item-text="e => `${e.name} ${e.lastName}`"
              item-value="id"
              prepend-inner-icon="mdi-magnify"
              background-color="#FFF"
              color="#0098E1"
              menu-props="auto"
              :label="`${$t('AddUser.addUserDialog.searchUser')}...`"
              single-line
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  medium
                  color="#EEF2F5"
                  @click="data.select"
                  @click:close="remove(data.item)"
                >
                  {{ data.item.name }} {{ data.item.lastName || '' }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-row>
          <v-row justify="center" class="mt-2">
            <v-btn color="#0098E1" class="submit-button" @click="linkUsers()" :loading="loadingLinkUsers">{{$t('AddUser.addUserDialog.linkUsers')}}</v-btn>
          </v-row>
        </v-card-text>
      </template>
      <template v-else-if="step === 'copyInvitation'">
        <v-card-title tag="p" class="new-user-dialog-title text-break mb-0 mt-0 text-center justify-center">
          <v-row>
            <v-col cols="8" offset="2">
              {{$t('NewCampaignInitialData.devicesAndServices.usersLinkedCopyTitle')}}
            </v-col>
            <v-col cols="2" class="px-0 text-end" align-self="start">
              <v-btn small icon @click="close" class="my-0">
                <v-icon color="#000000">close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="mt-0 dialog-content">
          <p class="text-center mb-4 users-linked-subtitle">{{$t('NewCampaignInitialData.devicesAndServices.usersLinkedCopySubtitle')}}</p>
          <v-row
            v-for="user in linkedUsersForCopy"
            :key="user.id"
            align="center"
            no-gutters
            class="mb-2 editor-invite-row"
          >
            <v-col cols="7" class="py-1 pr-2 text-left">
              <p class="mb-0 linked-user-name">{{ user.name }} {{ user.lastName || '' }}</p>
            </v-col>
            <v-col cols="5" class="py-1 d-flex justify-center">
              <v-btn
                color="#0098E1"
                small
                outlined
                class="copy-invitation-button"
                :loading="copyingInvitationUserId === user.id"
                @click="copyInvitationLink(user.id)"
              >
                <v-icon left small>mdi-content-copy</v-icon>
                {{$t('NewCampaignInitialData.devicesAndServices.copyInvitationLink')}}
              </v-btn>
            </v-col>
          </v-row>
          <v-row justify="center" class="mt-4">
            <v-btn color="#0098E1" class="submit-button" @click="close">
              {{$t('NewCampaignInitialData.devicesAndServices.continue')}}
            </v-btn>
          </v-row>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { CAMPAIGN_STEPS, WEBHOOKS_PATHS, EVENTS_TO_TRACK } from '../../../constants/const'
import { auth, firestore } from '../../../plugins/firebase'
import { trackEvent } from '../../../lib/track-events'
import { waitForInvitationUrl, copyTextToClipboard, fetchUserInvitationFields } from '../../../lib/invitation-link'
export default {
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    fromNewCampaign: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      steps: ['question', 'usersAssignment', 'copyInvitation'],
      step: 'question',
      assignedUsers: [],
      loadingLinkUsers: false,
      linkedUsersForCopy: [],
      copyingInvitationUserId: null
    }
  },
  created () {
  },
  computed: {
    sortedOrganizationUsers () {
      if (!this.$store.getters.organizationUsers || !Array.isArray(this.$store.getters.organizationUsers)) {
        return []
      }
      let users = [...this.$store.getters.organizationUsers]
      // Filter out users that are already in the campaign
      if (this.campaignId) {
        const campaign = this.$store.getters.getCampaign(this.campaignId)
        if (campaign && campaign.users) {
          const campaignUserIds = campaign.users.map(user => user.id || user)
          users = users.filter(user => !campaignUserIds.includes(user.id))
        }
      }
      return users.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase()
        const nameB = (b.name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    campaignEditors () {
      if (!this.campaignId) {
        return []
      }
      const campaign = this.$store.getters.getCampaign(this.campaignId)
      if (!campaign || !Array.isArray(campaign.users)) {
        return []
      }
      return campaign.users
        .filter(user => {
          if (!user || typeof user !== 'object' || !user.id) {
            return false
          }
          const isOwner = user.role === 'owner' || (user.roles && user.roles.owner)
          return !!user.editCampaignDataPermission && !isOwner
        })
        .sort((a, b) => {
          const nameA = `${a.name || ''} ${a.lastName || ''}`.toLowerCase()
          const nameB = `${b.name || ''} ${b.lastName || ''}`.toLowerCase()
          return nameA.localeCompare(nameB)
        })
    }
  },
  methods: {
    createUser () {
      this.$emit('update:dialog', false)
      trackEvent(EVENTS_TO_TRACK.CLICK_CREATE_NEW_USER_BUTTON, {
        campaignId: this.campaignId
      })
      if (this.fromNewCampaign) {
        this.$router.push({name: 'AddUser', params: {fromNewCampaign: true, originCampaignId: this.campaignId, newCampaignStep: CAMPAIGN_STEPS.EQUIPMENTS}})
      } else {
        this.$router.push({name: 'AddUser'})
      }
    },
    existingUsers () {
      trackEvent(EVENTS_TO_TRACK.CLICK_INVITE_EXISTING_USERS_BUTTON, {
        campaignId: this.campaignId
      })
      this.step = 'usersAssignment'
    },
    async linkUsers () {
      try {
        this.loadingLinkUsers = true

        const response = await fetch(`${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.LINK_USERS}`, {
          method: 'POST',
          body: JSON.stringify({
            campaignId: this.campaignId,
            users: this.assignedUsers,
            isNewCampaign: !!this.fromNewCampaign
          }),
          headers: {
            'auth-token': await auth.currentUser.getIdToken(),
            authorization: process.env.WEBHOOKS_BASIC_TOKEN,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const responseData = await response.json()

          // Update local store with processed users
          if (responseData.results && this.campaignId) {
            const processedUserIds = responseData.results.filter(user => user.status === 'processed').map(user => user.userId)

            if (processedUserIds.length > 0) {
              const db = await firestore
              const usersData = []

              // Get full user objects from Firestore
              for (const userId of processedUserIds) {
                try {
                  const userDoc = await db.collection('app/v1/users').doc(userId).get()
                  const userCampaign = await db.collection('app/v1/users').doc(userId).collection('campaigns').doc(this.campaignId).get()

                  if (userDoc.exists && userCampaign.exists) {
                    const uData = userDoc.data()
                    const userCampaignData = userCampaign.data()

                    let role = ''
                    if (uData.isAdmin) {
                      role = 'admin'
                    } else if (userCampaignData.roles) {
                      role = ['owner', 'manager', 'fieldman', 'farmhand'].find(r => !!userCampaignData.roles[r]) || ''
                    }

                    const devices = userCampaignData.devices ? userCampaignData.devices.map(device => device.id) : []

                    usersData.push({
                      id: userId,
                      name: uData.name,
                      lastName: uData.lastName || '',
                      email: uData.email,
                      phone: uData.phone,
                      reports: userCampaignData.reports,
                      devices: devices,
                      role: role,
                      editDataPermission: userCampaignData.editDataPermission,
                      editCampaignDataPermission: userCampaignData.editCampaignDataPermission,
                      photoUrl: uData.photoUrl,
                      channels: uData.channels,
                      settings: uData.settings,
                      lastUpdatedDate: uData.lastUpdatedDate || '',
                      isNewUser: uData.isNewUser === true
                    })
                  }
                } catch (error) {
                  console.error(`Error loading user data for ${userId}:`, error)
                }
              }

              this.$store.dispatch('updateCampaignUsers', {
                campaignId: this.campaignId,
                processedUsers: usersData
              })

              // Prevent duplicate users when adding to store
              const storeUsers = this.$store.getters.users
              const existingUserIds = new Set(storeUsers.map(user => user.id))
              const newUsers = usersData.filter(user => !existingUserIds.has(user.id))

              if (newUsers.length > 0) {
                this.$store.commit('setUsers', [...storeUsers, ...newUsers])
              }

              this.$emit('editors-linked', {
                userIds: processedUserIds,
                users: usersData
              })

              if (this.fromNewCampaign && usersData.length > 0) {
                this.linkedUsersForCopy = usersData
                this.step = 'copyInvitation'
                this.$root.snack.show({text: this.$t('UI.registerSuccess')})
                return
              }
            }
          }
        }

        this.$root.snack.show({text: this.$t('UI.registerSuccess')})
        this.$emit('update:dialog', false)
      } catch (error) {
        this.$root.snack.show({text: this.$t('UI.registerError'), error: true})
      } finally {
        this.loadingLinkUsers = false
      }
    },
    async resolveExpectNewUserForInvitation (userId) {
      const fields = await fetchUserInvitationFields(userId)
      if (fields.invitationKind === 'existing') {
        return false
      }
      return fields.invitationKind === 'new' || fields.invitationKind === 'unknown'
    },
    async copyInvitationLink (userId) {
      try {
        this.copyingInvitationUserId = userId
        const expectNewUser = await this.resolveExpectNewUserForInvitation(userId)
        const result = await waitForInvitationUrl(userId, { expectNewUser })
        trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_COPY_INVITATION_LINK_BUTTON, {
          campaignId: this.campaignId,
          userId,
          isNewUser: result.isNewUser,
          ready: result.ready
        })
        if (result.baseUrlMissing) {
          this.$root.snack.show({
            text: this.$t('NewCampaignInitialData.devicesAndServices.invitationLinkBaseUrlMissing'),
            error: true
          })
          return
        }
        if (!result.ready || !result.url) {
          this.$root.snack.show({
            text: this.$t('NewCampaignInitialData.devicesAndServices.invitationLinkNotReady'),
            error: true
          })
          return
        }
        await copyTextToClipboard(result.url)
        this.$root.snack.show({
          text: this.$t('NewCampaignInitialData.devicesAndServices.invitationLinkCopied')
        })
      } catch (error) {
        console.error('Error copying invitation link:', error)
        this.$root.snack.show({
          text: this.$t('NewCampaignInitialData.devicesAndServices.invitationLinkCopyError'),
          error: true
        })
      } finally {
        this.copyingInvitationUserId = null
      }
    },
    remove (item) {
      const index = this.assignedUsers.indexOf(item.id)
      if (index >= 0) this.assignedUsers.splice(index, 1)
    },
    close () {
      this.step = 'question'
      this.assignedUsers = []
      this.linkedUsersForCopy = []
      this.$emit('update:dialog', false)
    }
  },
  watch: {
    dialog (newVal) {
      this.$emit('update:dialog', newVal)
      if (!newVal) {
        this.step = 'question'
        this.assignedUsers = []
        this.linkedUsersForCopy = []
      }
    }
  }
}
</script>

<style scoped>
.dialog-card {
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.scrollable-content {
  padding-top: 10px;
  flex: 1;
  overflow-y: auto;
  max-height: 50vh;
  padding-right: 8px;
  .v-label {
    font-size: 20px;
  }
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.campaign-editors-section {
  border-top: 1px solid #E0E6EB;
  padding-top: 1rem;
}

.editors-title {
  font-family: 'Source Sans 3', sans-serif;
  font-weight: 400;
  font-size: 1.125rem;
  text-align: center;
}

.copy-invitation-button {
  text-transform: none;
  font-family: 'Source Sans 3', sans-serif !important;
  font-weight: 400;
  font-size: 1rem;
  border-radius: 8px;
}

.users-linked-subtitle {
  font-family: 'Source Sans 3', sans-serif;
  font-size: 1.2rem;
}

.linked-user-name {
  font-family: 'Source Sans 3', sans-serif;
  font-weight: 500;
  font-size: 1.1rem;
  color: #00202F;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editor-invite-row {
  flex-wrap: nowrap;
}
</style>



// WEBPACK FOOTER //
// AddUserDialog.vue?6415e638